<template>
  <div class="invoice-details-view">
    <title-component :size="1">
      <span v-translate>Invoice details</span>
      <action-dropdown-component
        v-if="actions.length"
        :actions="actions">
      </action-dropdown-component>
    </title-component>
    <!-- Customer -->
    <title-component
      v-if="invoiceCustomer.id"
      :size="3"
      :lineSeparator="'under'">
      <translate>Invoice customer</translate>
    </title-component>
    <grid-details-component
      v-if="invoiceCustomer.fullAddress"
      :display_spec="invoiceCustomerSpecs"
      :item="invoiceCustomer"
      :item_keys="invoice_customer_keys">
    </grid-details-component>
    <!-- Loader - customer -->
    <loader-component v-else :position="'relative'"></loader-component>
    <!-- Invoice -->
    <title-component
      v-if="invoiceCustomer.id"
      :size="3"
      :lineSeparator="'under'">
      <translate>Invoice</translate>
    </title-component>
    <grid-details-component
      v-if="invoice.id"
      :display_spec="invoiceSpecs"
      :item="invoice"
      :item_keys="invoice_keys">
    </grid-details-component>

    <table-component
      v-if="invoice.id"
      ref="articleTable"
      :content="invoice.articles"
      :headings="table_headings"
      :item_keys="article_keys"
      :display_spec="invoiceArticleSpecs"
      class="veridens-dashboard">
    </table-component>
    <!-- Loader - invoice table -->
    <loader-component v-else :position="'relative'"></loader-component>

    <!-- Modal (popup) for confirming cancellation -->
    <card-modal-component
      :show="showConfirmationModal"
      @deactivate="toggleConfirmationModal()"
      ref="confirmationModal"
      :title="'Confirmation'">
      <translate slot="card-content">
        Are you sure you want to cancel the invoice?
      </translate>
      <div class="field is-grouped" slot="card-footer">
        <div class="control">
          <submit-btn-component class="is-primary" slot="card-footer" @submit="cancelInvoice">Continue</submit-btn-component>
        </div>
        <div class="control">
          <button class="button is-danger is-outlined" slot="card-footer" @click="toggleConfirmationModal" v-translate>Cancel</button>
        </div>
      </div>
    </card-modal-component>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import { invoiceCustomerSpecs, invoiceSpecs, invoiceArticleSpecs } from '@/display_context/display_context'
  import TitleComponent from '@/components/layout/TitleComponent'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent'
  import { itemRefresher } from '@/mixins/mixins'
  import TableComponent from '@/components/layout/table/TableComponent'
  import ActionDropdownComponent from '@/components/layout/utility/ActionDropdownComponent.vue'
  import { InvoiceState } from '@/classes/case/Invoice'
  import LoaderComponent from '@/components/layout/utility/LoaderComponent'
  import CardModalComponent from '@/components/layout/modal/CardModalComponent'
  import SubmitBtnComponent from '@/components/layout/utility/SubmitBtnComponent'

  export default {
    name: 'invoice-details-view',
    mixins: [itemRefresher],
    data () {
      return {
        invoice_customer_keys: ['companyName', 'orgNumber', 'customerId', 'fullAddress', 'invoiceCustomerId'],
        invoice_keys: ['stateCell', 'marking', 'kiwaReference', 'customerReference', 'modified', 'invoiceNumber'],
        invoiceCustomerSpecs: invoiceCustomerSpecs,
        table_headings: ['Article No.', 'Description', 'Price', 'Qty', 'Sum'],
        article_keys: ['number', 'description', 'price', 'quantity', 'sum'],
        invoiceSpecs: invoiceSpecs,
        invoiceArticleSpecs: invoiceArticleSpecs,
        setAction: this.setInvoice,
        customer: {},
        invoiceCustomer: {},
        invoice: {},
        actions: [],
        showConfirmationModal: false
      }
    },
    props: {
      invoiceId: {
        required: true
      }
    },
    components: {
      TitleComponent,
      GridDetailsComponent,
      TableComponent,
      ActionDropdownComponent,
      LoaderComponent,
      CardModalComponent,
      SubmitBtnComponent
    },
    computed: {
      ...mapState({
        invoices: state => state.invoiceModule.invoices
      })
    },
    methods: {
      ...mapActions([
        'setInvoice',
        'setCustomer',
        'setHistoricalInvoiceCustomer',
        'postConfirmInvoice',
        'postCancelInvoice'
      ]),
      toggleConfirmationModal () {
        this.showConfirmationModal = !this.showConfirmationModal
      },
      addTotalRow () {
        if (this.$refs.articleTable) {
          this.$refs.articleTable.dynamic_rows.push({
            cells: [
              {
                value: 'Total:',
                colspan: 4,
                customClasses: 'is-pulled-right has-text-weight-bold'
              },
              {
                value: this.invoice.totalSum,
                customClasses: 'has-text-weight-bold',
                colspan: 1
              }
            ]
          })
        }
      },
      confirmInvoice () {
        this.postConfirmInvoice(this.invoiceId).then(invoice => {
          this.invoice = invoice
          this.setActions()
        }).catch((err) => {
          this.handleError(err)
        })
      },
      cancelInvoice (btn) {
        this.postCancelInvoice(this.invoiceId).then(invoice => {
          this.invoice = invoice
          this.setActions()
          this.showConfirmationModal = false
        }).catch((err) => {
          this.handleError(err)
        }).finally(() => {
          btn && btn.enable()
        })
      },
      setActions () {
        this.actions = []
        if (this.invoice.state.value === InvoiceState.DRAFT) {
          this.actions.push({
            type: 'ACTION',
            func: () => {
              this.confirmInvoice()
            },
            text: `Confirm`
          })
          this.actions.push({
            type: 'ROUTE',
            route: { name: 'InvoiceEditView', params: { invoiceId: this.invoice.id } },
            text: 'Edit...'
          })
        }
        if (this.invoice.state.value !== InvoiceState.CANCEL) {
          this.actions.push({
            type: 'ACTION',
            func: () => {
              this.toggleConfirmationModal()
            },
            text: `Cancel invoice`
          })
        }
        if (this.invoiceCustomer.id) {
          this.actions.push({
            type: 'ROUTE',
            route: { name: 'InvoiceCustomerCustomerListView', params: { invoiceCustomerId: this.invoiceCustomer.id } },
            text: 'Invoice customer...'
          })
        }
      },
      setInvoiceCustomerProps () {
        if (typeof this.customer.customerId !== 'undefined') {
          this.invoiceCustomer.customerId = this.customer.customerId
        }
        this.invoiceCustomer.fullAddress = `${this.invoiceCustomer.postalAddress}, ${this.invoiceCustomer.postalCode}, ${this.invoiceCustomer.postalCity}`
      },
      getInvoiceCustomer () {
        if (this.customer.isConnectedToInvoiceCustomer || typeof this.customer.customerId === 'undefined') {
          this.setHistoricalInvoiceCustomer(this.invoice.id).then((invoiceCustomer) => {
            this.invoiceCustomer = invoiceCustomer
            this.setActions()
            this.setInvoiceCustomerProps()
          }).catch((err) => {
            this.handleError(err)
          })
        }
      },
      getCustomer () {
        if (this.invoice.customerId !== null) {
          this.setCustomer(this.invoice.customerId).then((customer) => {
            this.customer = customer
            this.getInvoiceCustomer()
            this.addTotalRow()
            return null
          }).catch((err) => {
            this.handleError(err)
          })
          return
        }
        this.getInvoiceCustomer()
        this.addTotalRow()
      }
    },
    created () {
      let callbacks = [this.getCustomer, this.setActions]
      this.setOrRefreshItem('invoice', this.invoices, this.invoiceId, callbacks)
    }
  }
</script>

<style lang="sass">
  .invoice-details-view
    overflow: auto
</style>
